// src/components/OilPalmConcessionsComponent.js
import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';

const OilPalmConcessionsComponent = ({
  isVisible,
  opacity = 100,
  id,
  mapInstance,
  popupMapInstance, // optional separate popup container
  layerConfig
}) => {
  const popupRef = useRef(null);
  const isInitializedRef = useRef(false);
  const onMouseMove = useRef(null);
  const onMouseLeave = useRef(null);

  useEffect(() => {
    if (!mapInstance) return;
    const sourceId = id;
    const layerFillId = `${id}-fill`;
    const layerOutlineId = `${id}-outline`;

    // If already initialized or source already exists, do nothing
    if (isInitializedRef.current) return;
    if (mapInstance.getSource(sourceId)) return;

    const initializeLayer = () => {
      // Add the vector source for oil palm concessions
      if (!mapInstance.getSource(sourceId)) {
        mapInstance.addSource(sourceId, {
          type: 'vector',
          url: 'mapbox://ibau1.1b85mlxu'
        });
      }

      // Add the fill layer using the published source-layer name
      if (!mapInstance.getLayer(layerFillId)) {
        mapInstance.addLayer({
          id: layerFillId,
          type: 'fill',
          source: sourceId,
          'source-layer': 'gfw_oil_palm_v20191031-11u8ed',
          layout: {
            visibility: isVisible ? 'visible' : 'none'
          },
          paint: {
            'fill-color': layerConfig?.paint?.['fill-color'] || 'rgba(10, 168, 58, 0.5)',
            'fill-opacity': opacity / 100
          }
        });
      }

      // Add the outline layer using the same source-layer name
      if (!mapInstance.getLayer(layerOutlineId)) {
        mapInstance.addLayer({
          id: layerOutlineId,
          type: 'line',
          source: sourceId,
          'source-layer': 'gfw_oil_palm_v20191031-11u8ed',
          layout: {
            visibility: isVisible ? 'visible' : 'none'
          },
          paint: {
            'line-color': layerConfig?.paint?.['line-color'] || 'rgba(10, 168, 58, 1)',
            'line-width': 1,
            'line-opacity': opacity / 100
          }
        });
      }

      // Define mouse move handler for popups
      onMouseMove.current = (e) => {
        mapInstance.getCanvas().style.cursor = 'pointer';
        if (!e.features || e.features.length === 0) return;
        const feature = e.features[0];
        const properties = feature.properties || {};

        const content = `
          <div style="font-family: sans-serif; padding: 8px;">
            <div><strong>Name:</strong> ${properties.name || properties.NAME || 'N/A'}</div>
          </div>
        `;

        if (popupRef.current) {
          popupRef.current.remove();
        }

        const popupTarget = popupMapInstance || mapInstance;
        popupRef.current = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          maxWidth: '300px',
          className: 'oil-palm-popup'
        })
          .setLngLat(e.lngLat)
          .setHTML(content)
          .addTo(popupTarget);
      };

      // Define mouse leave handler to remove popups
      onMouseLeave.current = () => {
        mapInstance.getCanvas().style.cursor = '';
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }
      };

      // Attach event listeners on the fill layer
      mapInstance.on('mousemove', layerFillId, onMouseMove.current);
      mapInstance.on('mouseleave', layerFillId, onMouseLeave.current);

      isInitializedRef.current = true;
    };

    if (isVisible) {
      initializeLayer();
    }

    return () => {
      if (!mapInstance) return;
      if (onMouseMove.current) {
        mapInstance.off('mousemove', layerFillId, onMouseMove.current);
      }
      if (onMouseLeave.current) {
        mapInstance.off('mouseleave', layerFillId, onMouseLeave.current);
      }
      if (mapInstance.getLayer(layerFillId)) {
        try {
          mapInstance.removeLayer(layerFillId);
        } catch (err) {
          console.warn(`Error removing layer ${layerFillId}:`, err);
        }
      }
      if (mapInstance.getLayer(layerOutlineId)) {
        try {
          mapInstance.removeLayer(layerOutlineId);
        } catch (err) {
          console.warn(`Error removing layer ${layerOutlineId}:`, err);
        }
      }
      if (mapInstance.getSource(sourceId)) {
        try {
          mapInstance.removeSource(sourceId);
        } catch (err) {
          console.warn(`Error removing source ${sourceId}:`, err);
        }
      }
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, [isVisible, mapInstance, id, layerConfig, opacity, popupMapInstance]);

  // Update opacity when the prop changes
  useEffect(() => {
    if (!mapInstance) return;
    const layerFillId = `${id}-fill`;
    const layerOutlineId = `${id}-outline`;
    if (mapInstance.getLayer(layerFillId)) {
      mapInstance.setPaintProperty(layerFillId, 'fill-opacity', opacity / 100);
    }
    if (mapInstance.getLayer(layerOutlineId)) {
      mapInstance.setPaintProperty(layerOutlineId, 'line-opacity', opacity / 100);
    }
  }, [opacity, mapInstance, id]);

  // Update layer visibility when isVisible changes
  useEffect(() => {
    if (!mapInstance) return;
    const layerFillId = `${id}-fill`;
    const layerOutlineId = `${id}-outline`;
    const visibility = isVisible ? 'visible' : 'none';
    if (mapInstance.getLayer(layerFillId)) {
      mapInstance.setLayoutProperty(layerFillId, 'visibility', visibility);
    }
    if (mapInstance.getLayer(layerOutlineId)) {
      mapInstance.setLayoutProperty(layerOutlineId, 'visibility', visibility);
    }
  }, [isVisible, mapInstance, id]);

  return null;
};

export default OilPalmConcessionsComponent;
